"use client";

import { useEffect } from "react";
import type { LegacyAction } from "../components/TouchControls";

type KeyboardMode = "A" | "B";

/** The new hard drop is intentionally touch-only; PC bindings stay unchanged. */
export type KeyboardAction = Exclude<LegacyAction, "hardDrop">;
export type KeyBindings = Record<KeyboardAction, string[]>;

export const KEYBOARD_ACTIONS: readonly KeyboardAction[] = ["left", "right", "softDrop", "cw", "ccw"];

export const KEYBOARD_ACTION_LABELS: Readonly<Record<KeyboardAction, string>> = {
  left: "左移動",
  right: "右移動",
  softDrop: "ソフトドロップ",
  cw: "右回転",
  ccw: "左回転",
};

export const DEFAULT_KEY_BINDINGS: Readonly<KeyBindings> = {
  left: ["ArrowLeft"],
  right: ["ArrowRight"],
  softDrop: ["ArrowDown"],
  cw: ["ArrowUp", "Space"],
  ccw: ["IntlRo", "NonConvert", "ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight"],
};

const KEY_BINDINGS_STORAGE_KEY = "bwwk-key-bindings-v1";

export function cloneKeyBindings(bindings: Readonly<KeyBindings>): KeyBindings {
  return Object.fromEntries(
    KEYBOARD_ACTIONS.map((action) => [action, [...(bindings[action] ?? [])]]),
  ) as KeyBindings;
}

function isKeyBindings(value: unknown): value is Partial<KeyBindings> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return Object.values(value).every(
    (codes) => Array.isArray(codes) && codes.length > 0 && codes.every((code) => typeof code === "string" && code.length > 0),
  );
}

export function loadKeyBindings(): KeyBindings {
  if (typeof window === "undefined") return cloneKeyBindings(DEFAULT_KEY_BINDINGS);
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(KEY_BINDINGS_STORAGE_KEY) ?? "null");
    if (!isKeyBindings(parsed)) return cloneKeyBindings(DEFAULT_KEY_BINDINGS);
    const defaults = cloneKeyBindings(DEFAULT_KEY_BINDINGS);
    for (const action of KEYBOARD_ACTIONS) {
      const codes = parsed[action];
      if (codes) defaults[action] = [...codes];
    }
    return defaults;
  } catch {
    return cloneKeyBindings(DEFAULT_KEY_BINDINGS);
  }
}

export function saveKeyBindings(bindings: Readonly<KeyBindings>): void {
  try {
    window.localStorage.setItem(KEY_BINDINGS_STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // Keyboard configuration remains active for this session when storage is unavailable.
  }
}

export function formatKeyCode(code: string): string {
  const labels: Record<string, string> = {
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowDown: "↓",
    ArrowUp: "↑",
    Space: "Space",
    ShiftLeft: "左Shift",
    ShiftRight: "右Shift",
    ControlLeft: "左Ctrl",
    ControlRight: "右Ctrl",
    IntlRo: "ろ",
    NonConvert: "無変換",
    Enter: "Enter",
    Escape: "Esc",
    Tab: "Tab",
    Backspace: "Backspace",
  };
  if (labels[code]) return labels[code];
  const keyMatch = /^Key([A-Z])$/u.exec(code);
  if (keyMatch) return keyMatch[1];
  const digitMatch = /^Digit([0-9])$/u.exec(code);
  if (digitMatch) return digitMatch[1];
  const numpadMatch = /^Numpad([0-9])$/u.exec(code);
  if (numpadMatch) return `Num${numpadMatch[1]}`;
  return code.replace(/^Numpad/u, "Num ");
}

export function formatKeyCodes(codes: readonly string[]): string {
  return codes.map(formatKeyCode).join(" / ");
}

const mapAction = (event: KeyboardEvent, mode: KeyboardMode, bindings: Readonly<KeyBindings>): LegacyAction | null => {
  const code = event.code;

  for (const action of KEYBOARD_ACTIONS) {
    if (bindings[action]?.includes(code)) return action;
  }

  if (mode === "B") {
    if (code === "KeyZ") return "left";
    if (code === "KeyC") return "right";
    if (code === "KeyX") return "softDrop";
    if (code === "KeyN" || code === "Space") return "cw";
    if (code === "KeyB" || code === "ArrowUp") return "ccw";
    return null;
  }

  return null;
};

export function useLegacyKeyboard(
  enabled: boolean,
  onInput: (action: LegacyAction, held: boolean) => void,
  mode: KeyboardMode = "A",
  bindings: Readonly<KeyBindings> = DEFAULT_KEY_BINDINGS,
) {
  useEffect(() => {
    if (!enabled) return;

    const pressedCodes = new Map<string, LegacyAction>();

    const keyDown = (event: KeyboardEvent) => {
      const action = mapAction(event, mode, bindings);
      if (!action) return;
      event.preventDefault();
      if (pressedCodes.has(event.code)) return;
      pressedCodes.set(event.code, action);
      onInput(action, true);
    };

    const keyUp = (event: KeyboardEvent) => {
      const action = pressedCodes.get(event.code);
      if (!action) return;
      event.preventDefault();
      pressedCodes.delete(event.code);
      onInput(action, false);
    };

    const releaseAll = () => {
      for (const action of pressedCodes.values()) onInput(action, false);
      pressedCodes.clear();
    };

    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp, { passive: false });
    window.addEventListener("blur", releaseAll);

    return () => {
      releaseAll();
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", releaseAll);
    };
  }, [bindings, enabled, mode, onInput]);
}
